import { EventStatus } from '@puzzlehq/types';
import { getStatusColor } from '@puzzlehq/frontend-utils';
import { JoinStatus } from '@src/hooks/usePartJoin';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  HStack,
  Link,
  Progress,
  Separator,
  Spinner,
  Text,
  theme,
  VStack,
} from 'ui_2';
import { BiSolidCheckCircle, BiSolidXCircle } from 'react-icons/bi';
import { ReactNode, useMemo } from 'react';

type JoinStatusDialogProps = {
  joinRecordCount?: number;
  joinStatus?: JoinStatus;
  eventStatuses?: EventStatus[];
  open: boolean;
  setOpen: (open: boolean) => void;
  joinParts: () => void;
};

const statusMessages: Record<JoinStatus, ReactNode> = {
  Idle: (
    <>
      <Text font='regular-xs'>
        You must join your PART Records before purchasing this item. The Puzzle
        Arcade Store can only accept 14 PART records when purchasing. The join
        fees will be covered by Puzzle.
      </Text>
      <Text font='regular-xs'>
        Puzzle Arcade Tickets (PART) are based on the "UTXO Model" of blockchain
        where the PART of account is stored in multiple{' '}
        <Link
          href={'https://developer.aleo.org/concepts/fundamentals/records'}
          target='_blank'
          className='text-blue-600 hover:text-blue-800'
        >
          "records"
        </Link>{' '}
        stored on a user's device. The "UTXO Model" is analogous to cash.
      </Text>
    </>
  ),
  Joining:
    'Your PART Records are joining. This will take a few minutes. Please do not leave the page.',
  PartialSuccess: 'Some of the transactions failed. You may have to join again',
  Success:
    'Success! Your newly joined PART records are being sent to your wallet.',
  Error: 'An error occurred while joining your PART records.',
};

export const JoinStatusDialog = ({
  joinRecordCount,
  joinStatus = 'Idle',
  eventStatuses,
  open,
  setOpen,
  joinParts,
}: JoinStatusDialogProps) => {
  const isJoining = joinStatus === 'Joining';

  const { percentage } = useMemo(() => {
    let creatingCount = 0;
    let pendingCount = 0;
    let settledCount = 0;
    let errorCount = 0;

    if (eventStatuses === undefined) {
      return {
        creatingCount: undefined,
        pendingCount: undefined,
        settledCount: undefined,
        errorCount: undefined,
        percentage: undefined,
      };
    }

    eventStatuses.forEach((status) => {
      switch (status) {
        case EventStatus.Creating: {
          creatingCount += 1;
          return;
        }
        case EventStatus.Pending: {
          pendingCount += 1;
          return;
        }
        case EventStatus.Settled: {
          settledCount += 1;
          return;
        }
        case EventStatus.Failed: {
          errorCount += 1;
          return;
        }
      }
    });

    const percentage =
      (100 * (creatingCount + pendingCount * 2 + settledCount * 3)) /
      (eventStatuses.length * 3);

    console.log({
      creatingCount,
      pendingCount,
      settledCount,
      errorCount,
      percentage,
    });
    return {
      creatingCount,
      pendingCount,
      settledCount,
      errorCount,
      percentage,
    };
  }, [JSON.stringify(eventStatuses)]);

  return (
    <Dialog open={open} onOpenChange={isJoining ? undefined : setOpen}>
      <DialogOverlay className='bg-gray-500 opacity-50' />
      <DialogContent
        aria-describedby='join-status'
        className='w-4/5 max-w-[300px] bg-gray-50'
        style={{ borderRadius: 30 }}
        showOverlay={false}
        showClose={!isJoining}
      >
        <DialogHeader>
          <DialogTitle className='text-center'>
            <Text color='black' font='medium-sm' id='join-status'>
              {joinStatus === 'Idle' && 'Join Required!'}
              {joinStatus === 'Success' && 'Join Complete!'}
              {joinStatus === 'PartialSuccess' && 'Join Complete'}
              {joinStatus === 'Error' && 'Error Joining'}
              {joinStatus === 'Joining' && 'Join Status'}
            </Text>
          </DialogTitle>
        </DialogHeader>
        <VStack className='w-full gap-6'>
          <VStack className='w-full gap-1'>
            {typeof statusMessages[joinStatus] === 'string' ? (
              <Text font='regular-xs'>{statusMessages[joinStatus]}</Text>
            ) : (
              statusMessages[joinStatus]
            )}
          </VStack>

          {!eventStatuses && (
            <Button
              disabled={isJoining}
              onClick={joinParts}
              className='flex items-center gap-2'
            >
              Join {joinRecordCount} PART records
              {isJoining && <Spinner />}
            </Button>
          )}
          {eventStatuses && (
            <VStack className='w-full'>
              <VStack>
                <HStack className='w-full justify-between'>
                  <Text font='md'>Join #</Text>
                  <Text font='md'>Status</Text>
                </HStack>
                <Separator />
              </VStack>
              <VStack className='max-h-[245px] w-full gap-4 overflow-x-hidden overflow-y-scroll pt-4'>
                {eventStatuses.map((status, ix) => (
                  <HStack key={ix} className='justify-between'>
                    <Text font='sm'>#{ix + 1}</Text>
                    <HStack className='items-center gap-2'>
                      <Text font='sm' color={getStatusColor(status)}>
                        {status}
                      </Text>
                      {[EventStatus.Creating, EventStatus.Pending].includes(
                        status
                      ) && <Spinner />}
                      {status === EventStatus.Settled && (
                        <BiSolidCheckCircle className='text-green-400' />
                      )}
                      {status === EventStatus.Failed && (
                        <BiSolidXCircle className='text-red-400' />
                      )}
                    </HStack>
                  </HStack>
                ))}
              </VStack>
              {eventStatuses.length > 0 && percentage && (
                <Progress
                  className='mt-4'
                  color={theme.colors.green[600]}
                  value={percentage}
                />
              )}
            </VStack>
          )}
          {['Success', 'PartialSuccess', 'Error'].includes(joinStatus) && (
            <Button onClick={() => setOpen(false)}>Continue</Button>
          )}
        </VStack>
      </DialogContent>
    </Dialog>
  );
};
